import { v4 as uuidv4 } from 'uuid'

import { AUTH_ENDPOINTS, AUTH_STORAGE_KEYS } from '../constants/authConstants'
import type {
  LoginStepResponse,
  VerifyTotpResult,
  TotpSetup,
  RefreshTokenResponse,
  JWTPayload,
  StoredUser,
  VerifyOTPTokenPayload,
  LoginWithPasswordPayload,
} from '../types'

import axiosInstance from '@/api/axiosInstance'

// Hình dạng chung của mọi response backend: { status, message, error_code,
// data }. Interceptor của axiosInstance KHÔNG tự bóc lớp này — mỗi hàm gọi
// API phải tự làm, nếu không field bên trong luôn đọc ra undefined.
interface ApiEnvelope<T> {
  status: number | string
  message: string
  error_code: string
  data: T
  timestamp?: number
}

// Bóc payload ra khỏi envelope. Chấp nhận cả trường hợp `data` đã là payload
// trần (không bọc) để không vỡ nếu một endpoint nào đó trả khác quy ước.
const unwrapEnvelope = <T>(raw: ApiEnvelope<T> | T): T => {
  if (raw && typeof raw === 'object' && 'data' in raw) {
    return (raw as ApiEnvelope<T>).data
  }
  return raw as T
}

// Generate or get device ID
const getDeviceId = (): string => {
  const storedDeviceId = localStorage.getItem('device_id')
  if (storedDeviceId) {
    return storedDeviceId
  }

  const newDeviceId = uuidv4()
  localStorage.setItem('device_id', newDeviceId)
  return newDeviceId
}

// Decode JWT token to extract user info
const decodeJWT = (token: string): JWTPayload | null => {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error('Failed to decode JWT:', error)
    return null
  }
}

// Lưu token và thông tin admin sau khi đăng nhập thành công. Dùng chung cho
// nhánh TOTP tắt (token về ngay ở bước 1) và nhánh xác thực TOTP thành công.
const persistSession = (payload?: VerifyOTPTokenPayload): void => {
  if (!payload?.access_token || !payload?.refresh_token) {
    return
  }

  localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, payload.access_token)
  localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, payload.refresh_token)

  if (payload.admin_info) {
    const user: StoredUser = {
      id: payload.admin_info.admin_id.toString(),
      phone: payload.admin_info.phone,
      full_name: payload.admin_info.full_name,
      email: payload.admin_info.email,
      roles: [],
      permissions: payload.permissions ?? [],
      is_super_admin: payload.is_super_admin ?? false,
    }
    localStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(user))
    return
  }

  const decoded = decodeJWT(payload.access_token)
  if (decoded) {
    const user: StoredUser = {
      id: decoded.userId,
      phone: decoded.phone,
      full_name: decoded.phone,
      roles: [decoded.role],
      permissions: payload.permissions ?? [],
      is_super_admin: payload.is_super_admin ?? false,
    }
    localStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(user))
  }
}

// API Functions

// Bước 1: xác thực username + mật khẩu. Khi TOTP tắt (local/dev), backend
// cấp token ngay ở bước này nên lưu session luôn; ngược lại trả về mfa_token
// (và totp_setup nếu tài khoản chưa enroll) để màn hình chuyển sang bước 2.
export const loginWithPassword = async (
  username: string,
  password: string
): Promise<LoginStepResponse> => {
  const deviceId = getDeviceId()
  const { data } = await axiosInstance.post<ApiEnvelope<LoginStepResponse>>(
    AUTH_ENDPOINTS.LOGIN_WITH_PASSWORD,
    { username, password, device_id: deviceId } as LoginWithPasswordPayload
  )
  const step = unwrapEnvelope(data)

  if (!step.mfa_required && step.auth) {
    persistSession(step.auth)
  }

  return step
}

// Bước 2: xác thực mã TOTP bằng mfa_token nhận được ở bước 1.
export const verifyTotp = async (
  mfaToken: string,
  code: string
): Promise<VerifyTotpResult> => {
  const deviceId = getDeviceId()
  const { data } = await axiosInstance.post<ApiEnvelope<VerifyOTPTokenPayload>>(
    AUTH_ENDPOINTS.VERIFY_TOTP,
    { mfa_token: mfaToken, totp_code: code, device_id: deviceId }
  )
  const tokenPayload = unwrapEnvelope(data)
  persistSession(tokenPayload)

  return {
    status: typeof data.status === 'number' ? data.status : 200,
    message: data.message ?? 'Thành công',
    error_code: data.error_code ?? '',
    data: tokenPayload,
    timestamp: Date.now(),
  }
}

// Bắt đầu cấu hình lại TOTP (thêm/đổi thiết bị). Yêu cầu mật khẩu + mã hiện
// tại; trả về secret MỚI để quét trên các thiết bị cần dùng.
export const startTotpReconfigure = async (
  password: string,
  code: string
): Promise<TotpSetup> => {
  const { data } = await axiosInstance.post<ApiEnvelope<TotpSetup>>(
    AUTH_ENDPOINTS.TOTP_RECONFIGURE,
    { password, totp_code: code }
  )
  return unwrapEnvelope(data)
}

// Xác nhận đã quét được secret mới bằng một mã sinh từ secret đó.
export const confirmTotpReconfigure = async (code: string): Promise<void> => {
  await axiosInstance.post(AUTH_ENDPOINTS.TOTP_CONFIRM, { totp_code: code })
}

export const logout = async (): Promise<void> => {
  try {
    const accessToken = localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN)
    await axiosInstance.post(AUTH_ENDPOINTS.LOGOUT, {
      access_token: accessToken
    })
  } finally {
    // Clear tokens regardless of API response
    localStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN)
    localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
    localStorage.removeItem(AUTH_STORAGE_KEYS.USER)
    // Drop the device id too — the next login should generate a fresh one so a
    // session isn't carried across admins on the same machine.
    localStorage.removeItem('device_id')
  }
}

// Không có nơi nào gọi hàm này hiện tại — luồng làm mới token thật chạy
// trong interceptor của axiosInstance.ts (raw axios.post riêng, tự bóc
// envelope). Giữ hàm này đúng cho trường hợp có chỗ dùng tới sau này.
export const refreshToken = async (refreshToken: string): Promise<RefreshTokenResponse> => {
  const { data } = await axiosInstance.post<ApiEnvelope<VerifyOTPTokenPayload>>(
    AUTH_ENDPOINTS.REFRESH,
    { refresh_token: refreshToken }
  )
  const tokenPayload = unwrapEnvelope(data)

  if (tokenPayload?.access_token) {
    localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, tokenPayload.access_token)
  }

  return {
    status: typeof data.status === 'number' ? data.status : 200,
    message: data.message ?? 'Thành công',
    error_code: data.error_code ?? '',
    data: tokenPayload,
    timestamp: Date.now(),
  }
}

// Storage Helper Functions
export const getStoredToken = (): string | null => {
  return localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN)
}

export const getStoredRefreshToken = (): string | null => {
  return localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
}

export const getStoredUser = (): StoredUser | null => {
  const userStr = localStorage.getItem(AUTH_STORAGE_KEYS.USER)
  return userStr ? JSON.parse(userStr) : null
}

export const isAuthenticated = (): boolean => {
  return !!getStoredToken()
}
